import { useQuery } from "@tanstack/react-query";

export interface ImpersonationStatus {
  active: true;
  agent_email: string;
  expires_at: string;
  write_enabled: boolean;
  session_id: string;
}

interface AuthMeWithImpersonation {
  id: number;
  email: string;
  impersonation: ImpersonationStatus | null;
}

/**
 * Reads impersonation state from /auth/me. Returns null when no impersonation
 * is active.
 *
 * Design: Banner MUST read from /auth/me (not JWT) — the JWT `we` claim
 * does not refresh after enable_writes(), and the cookie is httpOnly anyway.
 * refetchOnWindowFocus: 'always' catches session changes on tab return.
 *
 * IMPORTANT — owns a dedicated query key. This banner mounts at the app root,
 * so it fires on public/unauthenticated routes too (e.g. the OAuth callback
 * page before its exchange sets cookies). It deliberately swallows a non-OK
 * /auth/me into a null-impersonation stub. If it shared the ["auth", "me"]
 * key with the dashboard's useCurrentUser, that plan-less stub would overwrite
 * the cache useCurrentUser reads for the plan — silently dropping the user to
 * the Free tier until a reload. A separate key keeps the two queries isolated.
 */
export function useImpersonationStatus(): ImpersonationStatus | null {
  const { data } = useQuery<AuthMeWithImpersonation>({
    queryKey: ["impersonation", "status"],
    queryFn: async () => {
      const res = await fetch("/api/v1/auth/me", { credentials: "include" });
      if (!res.ok) return { id: 0, email: "", impersonation: null };
      return res.json() as Promise<AuthMeWithImpersonation>;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: "always",
  });

  if (!data?.impersonation?.active) return null;
  return data.impersonation;
}
